/**
 * Hex Math Utilities
 * 
 * Implements hex coordinate system math for flat-topped hexagons.
 * Uses axial coordinates for storage and cube coordinates for calculations.
 * 
 * Reference: https://www.redblobgames.com/grids/hexagons/
 */

import { AxialCoord, CubeCoord, PixelCoord } from '../types/terrain';

/**
 * Hex size in pixels (distance from center to flat edge)
 */
export const HEX_SIZE = 30;

/**
 * Hex directions for flat-topped hexagons (axial coordinates)
 */
const HEX_DIRECTIONS: AxialCoord[] = [
  { q: 1, r: 0 },   // East
  { q: 1, r: -1 },  // Northeast
  { q: 0, r: -1 },  // Northwest
  { q: -1, r: 0 },  // West
  { q: -1, r: 1 },  // Southwest
  { q: 0, r: 1 },   // Southeast
];

/**
 * Convert axial coordinates to cube coordinates
 */
export function axialToCube(coord: AxialCoord): CubeCoord {
  const x = coord.q;
  const z = coord.r;
  const y = -x - z;
  return { x, y, z };
}

/**
 * Convert cube coordinates to axial coordinates
 */
export function cubeToAxial(coord: CubeCoord): AxialCoord {
  return {
    q: coord.x,
    r: coord.z,
  };
}

/**
 * Calculate distance between two hexes (in hexes)
 */
export function hexDistance(a: AxialCoord, b: AxialCoord): number {
  const ac = axialToCube(a);
  const bc = axialToCube(b);
  return Math.max(
    Math.abs(ac.x - bc.x),
    Math.abs(ac.y - bc.y),
    Math.abs(ac.z - bc.z)
  );
}

/**
 * Get the 6 neighboring hexes
 */
export function hexNeighbors(coord: AxialCoord): AxialCoord[] {
  return HEX_DIRECTIONS.map(dir => ({
    q: coord.q + dir.q,
    r: coord.r + dir.r,
  }));
}

/**
 * Get a specific neighbor by direction (0-5)
 */
export function hexNeighbor(coord: AxialCoord, direction: number): AxialCoord {
  const dir = HEX_DIRECTIONS[direction % 6];
  return {
    q: coord.q + dir.q,
    r: coord.r + dir.r,
  };
}

/**
 * Get all hexes at a specific distance (ring)
 */
export function hexRing(center: AxialCoord, radius: number): AxialCoord[] {
  if (radius === 0) {
    return [center];
  }

  const results: AxialCoord[] = [];
  
  // Start at a hex 'radius' steps away in direction 4 (southwest)
  let hex: AxialCoord = {
    q: center.q + HEX_DIRECTIONS[4].q * radius,
    r: center.r + HEX_DIRECTIONS[4].r * radius,
  };

  // Walk around the ring
  for (let i = 0; i < 6; i++) {
    for (let j = 0; j < radius; j++) {
      results.push({ ...hex });
      hex = hexNeighbor(hex, i);
    }
  }

  return results;
}

/**
 * Get all hexes within a radius (spiral)
 */
export function hexSpiral(center: AxialCoord, radius: number): AxialCoord[] {
  const results: AxialCoord[] = [center];
  
  for (let r = 1; r <= radius; r++) {
    results.push(...hexRing(center, r));
  }
  
  return results;
}

/**
 * Get hexes along a line between two points
 */
export function hexLine(a: AxialCoord, b: AxialCoord): AxialCoord[] {
  const distance = hexDistance(a, b);
  const results: AxialCoord[] = [];

  for (let i = 0; i <= distance; i++) {
    const t = distance === 0 ? 0 : i / distance;
    results.push(hexLerp(a, b, t));
  }

  return results;
}

/**
 * Linear interpolation between two hexes
 */
function hexLerp(a: AxialCoord, b: AxialCoord, t: number): AxialCoord {
  const ac = axialToCube(a);
  const bc = axialToCube(b);
  
  const cube: CubeCoord = {
    x: ac.x * (1 - t) + bc.x * t,
    y: ac.y * (1 - t) + bc.y * t,
    z: ac.z * (1 - t) + bc.z * t,
  };

  return cubeToAxial(cubeRound(cube));
}

/**
 * Round cube coordinates to nearest hex
 */
function cubeRound(cube: CubeCoord): CubeCoord {
  let rx = Math.round(cube.x);
  let ry = Math.round(cube.y);
  let rz = Math.round(cube.z);

  const xDiff = Math.abs(rx - cube.x);
  const yDiff = Math.abs(ry - cube.y);
  const zDiff = Math.abs(rz - cube.z);

  if (xDiff > yDiff && xDiff > zDiff) {
    rx = -ry - rz;
  } else if (yDiff > zDiff) {
    ry = -rx - rz;
  } else {
    rz = -rx - ry;
  }

  return { x: rx, y: ry, z: rz };
}

/**
 * Convert hex coordinates to pixel coordinates (center of hex)
 * For flat-topped hexagons
 */
export function hexToPixel(coord: AxialCoord, hexSize: number = HEX_SIZE): PixelCoord {
  const x = hexSize * (3/2 * coord.q);
  const y = hexSize * (Math.sqrt(3)/2 * coord.q + Math.sqrt(3) * coord.r);
  return { x, y };
}

/**
 * Convert pixel coordinates to hex coordinates
 * For flat-topped hexagons
 */
export function pixelToHex(pixel: PixelCoord, hexSize: number = HEX_SIZE): AxialCoord {
  const q = (2/3 * pixel.x) / hexSize;
  const r = (-1/3 * pixel.x + Math.sqrt(3)/3 * pixel.y) / hexSize;
  
  const cube: CubeCoord = {
    x: q,
    y: -q - r,
    z: r,
  };

  return cubeToAxial(cubeRound(cube));
}

/**
 * Get the 6 corner points of a hex in pixel coordinates
 * For flat-topped hexagons
 */
export function hexCorners(coord: AxialCoord, hexSize: number = HEX_SIZE): PixelCoord[] {
  const center = hexToPixel(coord, hexSize);
  const corners: PixelCoord[] = [];
  
  for (let i = 0; i < 6; i++) {
    const angleDeg = 60 * i;
    const angleRad = (Math.PI / 180) * angleDeg;
    corners.push({
      x: center.x + hexSize * Math.cos(angleRad),
      y: center.y + hexSize * Math.sin(angleRad),
    });
  }
  
  return corners;
}

/**
 * Create a key string from hex coordinates for use in Maps
 */
export function hexKey(coord: AxialCoord): string {
  return `${coord.q},${coord.r}`;
}

/**
 * Parse a hex key string back to coordinates
 */
export function parseHexKey(key: string): AxialCoord {
  const [q, r] = key.split(',').map(Number);
  return { q, r };
}

/**
 * Check if a hex coordinate is within map bounds
 */
export function isInBounds(coord: AxialCoord, width: number, height: number): boolean {
  return coord.q >= 0 && coord.q < width && coord.r >= 0 && coord.r < height;
}

/**
 * Isometric transformation constant
 * Tilt angle: rotate around X-axis to tip hexes backward
 * Set to 0 for flat top-down view (no tilt)
 */
const ISOMETRIC_TILT_ANGLE = 0; // 0 degrees = flat top-down view

/**
 * Convert hex coordinates to pixel coordinates in isometric view
 * Simply tips the hexes back by rotating around the X-axis
 */
export function hexToPixelIsometric(coord: AxialCoord, hexSize: number = HEX_SIZE): PixelCoord {
  // First get flat coordinates
  const flat = hexToPixel(coord, hexSize);
  
  // Tip back: rotate around X-axis (horizontal)
  // This compresses Y and creates perspective
  const cosX = Math.cos(ISOMETRIC_TILT_ANGLE);
  const sinX = Math.sin(ISOMETRIC_TILT_ANGLE);
  
  // X stays the same, Y gets compressed by cos(angle)
  // This creates the "tipped back" effect
  const x2d = flat.x;
  const y2d = flat.y * cosX; // Compress Y to create perspective
  
  return { x: x2d, y: y2d };
}

/**
 * Get the 6 corner points of a hex in isometric pixel coordinates
 * Tips the hex back by compressing the Y coordinate
 */
export function hexCornersIsometric(coord: AxialCoord, hexSize: number = HEX_SIZE): PixelCoord[] {
  const center = hexToPixelIsometric(coord, hexSize);
  const corners: PixelCoord[] = [];
  
  // Get flat hex corners first
  const flatCorners = hexCorners(coord, hexSize);
  const flatCenter = hexToPixel(coord, hexSize);
  
  // Tilt angle for perspective
  const cosX = Math.cos(ISOMETRIC_TILT_ANGLE);
  
  // Transform each corner - just compress Y to tip back
  for (const flatCorner of flatCorners) {
    // Calculate relative position from center
    const relX = flatCorner.x - flatCenter.x;
    const relY = flatCorner.y - flatCenter.y;
    
    // Apply tilt: X stays same, Y gets compressed
    corners.push({
      x: center.x + relX,
      y: center.y + relY * cosX, // Compress Y to create tipped-back perspective
    });
  }
  
  return corners;
}

/**
 * Convert isometric pixel coordinates back to hex coordinates
 * Reverses the isometric transformation (uncompresses Y) then converts to hex
 */
export function pixelToHexIsometric(pixel: PixelCoord, hexSize: number = HEX_SIZE): AxialCoord {
  // Reverse the isometric transformation: uncompress Y
  // In isometric, Y was compressed by cos(60°) = 0.5
  const cosX = Math.cos(ISOMETRIC_TILT_ANGLE);
  const flatY = pixel.y / cosX; // Uncompress Y
  
  // Now convert flat coordinates to hex
  return pixelToHex({ x: pixel.x, y: flatY }, hexSize);
}

/**
 * Calculate depth value for z-ordering in isometric view
 * Higher values should be drawn first (behind), lower values drawn last (in front)
 */
export function hexDepthIsometric(coord: AxialCoord, center: AxialCoord): number {
  // In isometric view, hexes further away (higher q+r) should be drawn first
  // Hexes closer to camera (lower q+r) should be drawn last
  const cube = axialToCube(coord);
  const centerCube = axialToCube(center);
  
  // Calculate depth based on distance from center and position
  // Use a combination of distance and position to ensure proper ordering
  const depth = cube.x + cube.y + cube.z - (centerCube.x + centerCube.y + centerCube.z);
  
  return depth;
}

